import React, { useEffect, useRef, useState } from "react";
import "./RunQueue.css";
import {
  PHASES,
  describeRun,
  normalizePhase,
  runIdTail,
} from "../RSAAnalysis/InternalComponents/runLabel";

/**
 * Presentational pieces shared by the dashboard run tile (RSAAnalysis.js),
 * the dashboard hero (AnalysisDashboard.jsx) and the Multi-project runs rows
 * (RunQueuePanel.jsx) — the 2026-09-08 mockup:
 *
 *   RunTitle       "SR143s1-1 · MP 0–12.3" (label) or "<Project> · SSD, ISD"
 *   AnalysisChips  one chip per analysis code
 *   PhaseChip      Pre-process (blue) / Analysis (amber) / Deliverables (green)
 *                  / Queued (grey) + optional percent + bar
 *   RunIdTail      "…1134-4595" in mono with a copy-to-clipboard control; the
 *                  full id is the tooltip
 */

export const phaseClass = (phase) => {
  const key = normalizePhase(phase) || "queued";
  return `run-phase-chip run-phase-chip--${PHASES[key]?.tone || "queued"}`;
};

export function AnalysisChips({ analyses = [], className = "", max = 6 }) {
  const list = (analyses || []).map(String).filter(Boolean);
  if (list.length === 0) return null;
  const shown = list.slice(0, max);
  const extra = list.length - shown.length;
  return (
    <span
      className={`run-analysis-chips${className ? ` ${className}` : ""}`}
      data-testid="run-analysis-chips"
      title={list.join(", ")}
    >
      {shown.map((code) => (
        <span key={code} className="run-analysis-chip">
          {code}
        </span>
      ))}
      {extra > 0 && <span className="run-analysis-chip run-analysis-chip--more">+{extra}</span>}
    </span>
  );
}

/**
 * `phase` is one of queued|preprocess|analysis|deliverables|done|failed (or an
 * alias normalizePhase understands). `label` overrides the display text
 * (backend `phase_label`). `percent` null hides the number + bar.
 */
export function PhaseChip({
  phase,
  label = null,
  percent = null,
  showBar = true,
  title = null,
  className = "",
}) {
  const key = normalizePhase(phase);
  if (!key && !label) return null;
  const text = label || PHASES[key]?.label || String(phase);
  const pct =
    percent == null || !Number.isFinite(Number(percent))
      ? null
      : Math.max(0, Math.min(100, Math.round(Number(percent))));
  return (
    <span
      className={`run-phase${className ? ` ${className}` : ""}`}
      data-testid="run-phase"
      data-phase={key || ""}
      title={title || text}
    >
      <span className={phaseClass(key || "queued")}>{text}</span>
      {pct != null && (
        <>
          <span className="run-phase-percent">{pct}%</span>
          {showBar && (
            <span className="run-phase-bar" aria-hidden="true">
              <span
                className={`run-phase-bar-fill run-phase-bar-fill--${PHASES[key]?.tone || "queued"}`}
                style={{ width: `${pct}%` }}
              />
            </span>
          )}
        </>
      )}
    </span>
  );
}

const copyText = async (text) => {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    /* fall through to the execCommand path */
  }
  try {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "");
    el.style.position = "fixed";
    el.style.opacity = "0";
    document.body.appendChild(el);
    el.select();
    const ok = document.execCommand && document.execCommand("copy");
    document.body.removeChild(el);
    return Boolean(ok);
  } catch (_) {
    return false;
  }
};

/** `text` overrides the shown tail (the hero shows the full short id); the full id is always the tooltip + what gets copied. */
export function RunIdTail({ runId, text = null, className = "", copy = true }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  const id = String(runId ?? "").trim();
  if (!id) return null;
  const tail = text || runIdTail(id);
  const onCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyText(id);
    setCopied(ok);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span
      className={`run-id-tail${className ? ` ${className}` : ""}`}
      title={id}
      data-testid="run-id-tail"
    >
      <code className="run-id-tail-text">{tail}</code>
      {copy && (
        <button
          type="button"
          className="run-id-tail-copy"
          onClick={onCopy}
          title={copied ? "Copied" : `Copy run id ${id}`}
          aria-label={copied ? "Run id copied" : "Copy run id"}
        >
          <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"}`} aria-hidden />
        </button>
      )}
    </span>
  );
}

/**
 * Title of a run tile / hero. `run` is the dashboard run, `progress` its
 * progress GET, `entry` its queue row (any may be null). Renders the label's
 * display when there is one, else "<project name> · <analysis types>".
 */
export function RunTitle({
  run,
  progress = null,
  entry = null,
  as: Tag = "h4",
  className = "",
  withSubtitle = true,
}) {
  const d = describeRun(run, progress, entry);
  const tooltip = d.hasLabel
    ? `${d.title} — ${d.projectName}${d.subtitle ? ` · ${d.subtitle}` : ""}`
    : d.title;
  return (
    <>
      <Tag
        className={`run-title${d.hasLabel ? " run-title--labelled" : " run-title--fallback"}${className ? ` ${className}` : ""}`}
        title={tooltip}
        data-testid="run-title"
      >
        {d.title}
      </Tag>
      {withSubtitle && d.subtitle && (
        <span className="run-subtitle" data-testid="run-subtitle" title={d.subtitle}>
          {d.subtitle}
        </span>
      )}
    </>
  );
}

export default RunTitle;
